/**
 * Range Slider Component
 *
 * Custom range slider that syncs with native <input type="range"> for proper form submission.
 * Automatically updates output display and sets CSS custom property for progress styling.
 *
 * Features:
 * - Calculates normalized progress (0-1) from min/max values
 * - Sets CSS custom property (--_hs---progress) on wrapper
 * - Updates optional output element with current value
 * - Prevents duplicate initialization with dataset flag
 * - Works with negative min values and custom step sizes
 *
 * HTML Structure:
 *   <div data-hs-form-range="wrapper">
 *     <input type="range" min="0" max="100" value="50">
 *     <span data-hs-form-range="output">50</span>
 *   </div>
 *
 * CSS Usage:
 *   .range-fill { width: calc(var(--_hs---progress) * 100%); }
 */

import { querySelectorAll, querySelector } from '@utils';
import globalConfig from '@config';

interface ModuleConfig {
  attributes: {
    elements: Record<string, { primary: string; aliases?: string[] }>;
  };
  cssVars?: {
    progress?: string;
  };
}

export function init(config: ModuleConfig) {
  // Centralized cleanup tracking
  const cleanup = {
    handlers: [],
    domContentLoadedHandler: null,
  };

  const addHandler = (element: Element, event: string, handler: EventListener) => {
    element.addEventListener(event, handler);
    cleanup.handlers.push({ element, event, handler });
  };

  // Helper: Get CSS var name with global prefix
  function getProgressVarName(): string {
    const prefix = globalConfig._global.cssVars.prefix;
    const varName = config.cssVars?.progress || 'progress';
    return `${prefix}${varName}`;
  }

  // Initialize all range components
  function initRangeComponents() {
    const rangeWrappers = querySelectorAll(config, 'wrapper');

    rangeWrappers.forEach((wrapper) => {
      const htmlWrapper = wrapper as HTMLElement;
      // Prevent duplicate initialization
      if (htmlWrapper.dataset.hsFormRangeInit) return;
      htmlWrapper.dataset.hsFormRangeInit = 'true';

      initSingleRange(htmlWrapper, addHandler);
    });
  }

  // Initialize a single range component
  function initSingleRange(
    wrapper: HTMLElement,
    addHandler: (element: Element, event: string, handler: EventListener) => void
  ) {
    const input = wrapper.querySelector("input[type='range']") as HTMLInputElement;
    if (!input) {
      console.warn('[range] Missing <input type="range"> element in wrapper', wrapper);
      return;
    }

    const output = querySelector(config, 'output', wrapper);

    // Update function
    const update = () => {
      const min = input.min ? +input.min : 0;
      const max = input.max ? +input.max : 100;
      const val = +input.value || min;
      const progress = (val - min) / (max - min);

      // Set CSS custom property using global config
      wrapper.style.setProperty(getProgressVarName(), String(progress));

      // Update output text if exists
      if (output) {
        output.textContent = String(val);
      }
    };

    // Add input event listener
    addHandler(input, 'input', update);

    // Initialize on load
    update();
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    cleanup.domContentLoadedHandler = initRangeComponents;
    document.addEventListener('DOMContentLoaded', cleanup.domContentLoadedHandler);
  } else {
    initRangeComponents();
  }

  return {
    result: 'range initialized',
    destroy: () => {
      // Remove DOMContentLoaded listener
      if (cleanup.domContentLoadedHandler) {
        document.removeEventListener('DOMContentLoaded', cleanup.domContentLoadedHandler);
        cleanup.domContentLoadedHandler = null;
      }

      // Remove all event listeners
      cleanup.handlers.forEach(({ element, event, handler }) => {
        element.removeEventListener(event, handler);
      });
      cleanup.handlers.length = 0;

      // Remove init flags
      const rangeWrappers = querySelectorAll(config, 'wrapper');
      rangeWrappers.forEach((wrapper) => {
        delete (wrapper as HTMLElement).dataset.hsFormRangeInit;
      });
    },
  };
}
